import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Progress,
  Button,
  Space,
  Typography,
  Tag,
  Spin,
  Empty,
  message,
  Divider
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  CrownOutlined,
  BarChartOutlined,
  DashboardOutlined,
  DownloadOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import axios from 'axios';
import { BASE_URL } from '../../Constants';
import AppContext from '../../AppContext';
import { formatDate } from "../shared/DateField";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { Title: AntTitle, Text } = Typography;

const EventAnalyticsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, isVirtual } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [event, setEvent] = useState(null);
  const [hasLiveDashboard, setHasLiveDashboard] = useState(true);
  const [hasPostEventAnalytics, setHasPostEventAnalytics] = useState(true);

  useEffect(() => {
    if (profile?.companyId) {
      fetchAnalytics();
    }
  }, [id, profile?.companyId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const companyId = profile?.companyId;

      console.log('Fetching analytics for event:', id);
      console.log('Company ID:', companyId);

      // Fetch event details and analytics
      // Note: Authorization header is set globally via axios.defaults in AppContext
      const [eventResponse, analyticsResponse] = await Promise.all([
        axios.get(`${BASE_URL}/Event/${id}?companyId=${companyId}`),
        axios.get(`${BASE_URL}/Event/${id}/statistics?companyId=${companyId}`)
      ]);

      setEvent(eventResponse.data);
      setAnalytics(analyticsResponse.data);
      await loadEntitlements(eventResponse.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (error.response?.status === 401) {
        message.error('Authentication failed. Please login again.');
      } else {
        message.error('Failed to load analytics data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Mirrors EventRegistrationsPage.jsx's entitlement resolution: legacy/non-virtual companies
  // are grandfathered in (no entitlement lookup applies to them); virtual companies match by the
  // event's own entitlementId, falling back to the first active entitlement.
  const loadEntitlements = async (eventData) => {
    try {
      if (!isVirtual) {
        setHasLiveDashboard(true);
        setHasPostEventAnalytics(true);
        return;
      }
      const entitlementsRes = await axios.get(`${BASE_URL}/Company/my-entitlements`);
      const entitlements = entitlementsRes.data || [];
      const eventEntitlement = eventData?.entitlementId
        ? entitlements.find((e) => e.id === eventData.entitlementId)
        : null;
      const relevantEntitlement = eventEntitlement || entitlements.find((e) => e.status === 'active');
      setHasLiveDashboard(!!relevantEntitlement?.hasLiveDashboard);
      setHasPostEventAnalytics(!!relevantEntitlement?.hasPostEventAnalytics);
    } catch (err) {
      console.error('Error fetching entitlements:', err);
      setHasLiveDashboard(false);
      setHasPostEventAnalytics(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
    message.success('Analytics refreshed');
  };

  const handleExport = () => {
    // TODO: Implement CSV/PDF export
    message.info('Export functionality coming soon');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large">
          <div style={{ padding: '50px' }} />
        </Spin>
      </div>
    );
  }

  if (!analytics || !event) {
    return (
      <div style={{ padding: '24px' }}>
        <Empty description="No analytics data available" />
      </div>
    );
  }

  // Prepare chart data
  const registrationChartData = {
    labels: ['Confirmed', 'Pending', 'Cancelled'],
    datasets: [{
      label: 'Registrations',
      data: [
        analytics.confirmedRegistrations,
        analytics.pendingRegistrations,
        analytics.cancelledRegistrations
      ],
      backgroundColor: [
        'rgba(52, 211, 153, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderColor: [
        'rgb(52, 211, 153)',
        'rgb(251, 191, 36)',
        'rgb(239, 68, 68)'
      ],
      borderWidth: 2
    }]
  };

  const ticketTypeChartData = {
    labels: ['VIP', 'Regular'],
    datasets: [{
      label: 'Ticket Types',
      data: [analytics.vipRegistrations, analytics.regularRegistrations],
      backgroundColor: [
        'rgba(139, 92, 246, 0.8)',
        'rgba(59, 130, 246, 0.8)'
      ],
      borderColor: [
        'rgb(139, 92, 246)',
        'rgb(59, 130, 246)'
      ],
      borderWidth: 2
    }]
  };

  // Prepare hourly check-in chart
  const hourlyLabels = analytics.checkInsByHour ? Object.keys(analytics.checkInsByHour).sort() : [];
  const hourlyData = hourlyLabels.map(hour => analytics.checkInsByHour[hour]);

  const hourlyChartData = {
    labels: hourlyLabels,
    datasets: [{
      label: 'Check-ins per Hour',
      data: hourlyData,
      fill: true,
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgb(59, 130, 246)',
      borderWidth: 2,
      tension: 0.4,
      pointBackgroundColor: 'rgb(59, 130, 246)',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  };

  // Prepare gate breakdown table data
  const gateTableData = analytics.checkInsByGate
    ? Object.entries(analytics.checkInsByGate).map(([gate, count], index) => ({
      key: index,
      gate: gate,
      checkIns: count,
      percentage: analytics.totalCheckIns > 0
        ? ((count / analytics.totalCheckIns) * 100).toFixed(1)
        : 0
    }))
    : [];

  const gateColumns = [
    {
      title: 'Gate Name',
      dataIndex: 'gate',
      key: 'gate',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Check-ins',
      dataIndex: 'checkIns',
      key: 'checkIns',
      sorter: (a, b) => a.checkIns - b.checkIns,
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      sorter: (a, b) => a.percentage - b.percentage,
      render: (percentage, record) => (
        <div style={{ width: '150px' }}>
          <Progress
            percent={parseFloat(percentage)}
            size="small"
            status="active"
            format={(percent) => `${percent}%`}
          />
        </div>
      )
    }
  ];

  const notCheckedIn = analytics.totalRegistrations - analytics.totalCheckIns;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/dashboard/events')}
          >
            Back to Events
          </Button>
        </Space>

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <AntTitle level={2} style={{ margin: 0 }}>
              <BarChartOutlined /> Event Analytics
            </AntTitle>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              {event.title}
            </Text>
          </div>

          <Space>
            <Button
              icon={refreshing ? <SyncOutlined spin /> : <SyncOutlined />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Refresh
            </Button>
            {/* <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export Report
            </Button> */}
          </Space>
        </div>

        {event.eventType === 'marriage' && (event.groomName || event.brideName) && (
          <Card size="small" style={{ marginTop: '16px', backgroundColor: '#fff0f6', borderColor: '#ffadd2' }}>
            <Text strong style={{ color: '#c41d7f' }}>
              💍 {event.groomName || '—'} &amp; {event.brideName || '—'}
            </Text>
            {event.startDate && (
              <Text type="secondary" style={{ marginLeft: '12px' }}>
                {formatDate(String(event.startDate).slice(0, 10), "en", event.calendarPreference, event.hijriAdjustmentDays || 0)}
              </Text>
            )}
          </Card>
        )}
      </div>

      {/* Key Metrics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Capacity"
              value={analytics.totalCapacity}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
            <Progress
              percent={parseFloat(analytics.registrationPercentage.toFixed(1))}
              size="small"
              status={analytics.registrationPercentage >= 100 ? 'exception' : 'active'}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Registrations"
              value={analytics.totalRegistrations}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                {analytics.vipRegistrations} VIP • {analytics.regularRegistrations} Regular
              </Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                {analytics.totalCheckIns} Checked in • 
                <ClockCircleOutlined style={{ color: '#faad14', marginLeft: '4px', marginRight: '4px' }} />
                {analytics.totalRegistrations - analytics.totalCheckIns} Not checked in
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Check-ins"
              value={analytics.totalCheckIns}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Progress
              percent={parseFloat(analytics.checkInPercentage.toFixed(1))}
              size="small"
              status="active"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Not Checked In"
              value={notCheckedIn}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: notCheckedIn > 0 ? '#faad14' : '#52c41a' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Awaiting check-in
            </Text>
          </Card>
        </Col>
      </Row>

      {!hasLiveDashboard && !hasPostEventAnalytics ? (
        <Card style={{ textAlign: 'center', padding: '24px' }}>
          <Text type="secondary">{t('eventManagement.actions.analyticsNotIncluded')}</Text>
        </Card>
      ) : (
        <>
      {hasPostEventAnalytics ? (
      <>
      {/* Registration Breakdown */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {/* <Col xs={24} lg={12}>
          <Card title="Registration Status Breakdown" bordered={false}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pie data={registrationChartData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} />
                </div>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text><CheckCircleOutlined style={{ color: '#52c41a' }} /> Confirmed</Text>
                      <Text strong>{analytics.confirmedRegistrations}</Text>
                    </div>
                    <Progress
                      percent={(analytics.confirmedRegistrations / analytics.totalRegistrations * 100).toFixed(1)}
                      strokeColor="#52c41a"
                      showInfo={false}
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text><ClockCircleOutlined style={{ color: '#faad14' }} /> Pending</Text>
                      <Text strong>{analytics.pendingRegistrations}</Text>
                    </div>
                    <Progress
                      percent={(analytics.pendingRegistrations / analytics.totalRegistrations * 100).toFixed(1)}
                      strokeColor="#faad14"
                      showInfo={false}
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text><WarningOutlined style={{ color: '#ff4d4f' }} /> Cancelled</Text>
                      <Text strong>{analytics.cancelledRegistrations}</Text>
                    </div>
                    <Progress
                      percent={(analytics.cancelledRegistrations / analytics.totalRegistrations * 100).toFixed(1)}
                      strokeColor="#ff4d4f"
                      showInfo={false}
                    />
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col> */}

        <Col xs={24} lg={12}>
          <Card title="Ticket Type Distribution" bordered={false}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pie data={ticketTypeChartData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} />
                </div>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <Card size="small" style={{ backgroundColor: '#f6f5ff' }}>
                    <Statistic
                      title={<><CrownOutlined /> VIP Tickets</>}
                      value={analytics.vipRegistrations}
                      valueStyle={{ color: '#722ed1' }}
                    />
                    <Text type="secondary">
                      {((analytics.vipRegistrations / analytics.totalRegistrations) * 100).toFixed(1)}% of total
                    </Text>
                  </Card>

                  <Card size="small" style={{ backgroundColor: '#f0f9ff' }}>
                    <Statistic
                      title={<><UserOutlined /> Regular Tickets</>}
                      value={analytics.regularRegistrations}
                      valueStyle={{ color: '#1890ff' }}
                    />
                    <Text type="secondary">
                      {((analytics.regularRegistrations / analytics.totalRegistrations) * 100).toFixed(1)}% of total
                    </Text>
                  </Card>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Check-in Analytics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24}>
          <Card
            title={
              <Space>
                <DashboardOutlined />
                <span>Check-in Timeline</span>
                <Tag color="blue">{analytics.totalCheckIns} Total Check-ins</Tag>
              </Space>
            }
            bordered={false}
          >
            {hourlyLabels.length > 0 ? (
              <div style={{ height: '350px' }}>
                <Line
                  data={hourlyChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => `Check-ins: ${context.parsed.y}`
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <Empty description="No check-in data yet" />
            )}
          </Card>
        </Col>
      </Row>
      </>
      ) : (
        <Card style={{ textAlign: 'center', padding: '24px', marginBottom: '24px' }}>
          <Text type="secondary">{t('eventManagement.actions.analyticsNotIncluded')}</Text>
        </Card>
      )}

      {hasLiveDashboard ? (
      <>
      {/* Gate Performance */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Gate Performance" bordered={false}>
            {gateTableData.length > 0 ? (
              <Table
                columns={gateColumns}
                dataSource={gateTableData}
                pagination={false}
                size="middle"
              />
            ) : (
              <Empty description="No gate check-in data available" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Scanner Statistics" bordered={false}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text type="secondary">Total Scans</Text>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff' }}>
                  {analytics.totalScans}
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text><CheckCircleOutlined style={{ color: '#52c41a' }} /> Valid Scans</Text>
                <Text strong style={{ color: '#52c41a' }}>{analytics.validScans}</Text>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text><WarningOutlined style={{ color: '#ff4d4f' }} /> Invalid Scans</Text>
                <Text strong style={{ color: '#ff4d4f' }}>{analytics.invalidScans}</Text>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text><ThunderboltOutlined style={{ color: '#faad14' }} /> Duplicate Scans</Text>
                <Text strong style={{ color: '#faad14' }}>{analytics.duplicateScans}</Text>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <Progress
                percent={analytics.totalScans > 0 ? ((analytics.validScans / analytics.totalScans) * 100).toFixed(1) : 0}
                status="active"
                strokeColor="#52c41a"
                format={(percent) => `${percent}% Valid`}
              />
            </Space>
          </Card>
        </Col>
      </Row>
      </>
      ) : (
        <Card style={{ textAlign: 'center', padding: '24px', marginBottom: '24px' }}>
          <Text type="secondary">{t('eventManagement.actions.analyticsNotIncluded')}</Text>
        </Card>
      )}
        </>
      )}

      {/* Footer Info */}
      <Card size="small" style={{ backgroundColor: '#fafafa', textAlign: 'center' }}>
        <Text type="secondary">
          Last updated: {new Date(analytics.generatedAt).toLocaleString()} •
          Event Status: <Tag color={event.status === 'active' ? 'green' : 'orange'}>{event.status?.toUpperCase()}</Tag>
        </Text>
      </Card>
    </div>
  );
};

export default EventAnalyticsPage;
